import React, {
  SyntheticEvent, useCallback, useEffect, useRef,
} from 'react';
import { debounce } from 'lodash';
import { Link } from 'core/typings';
import './styles.scss';

interface AnchorProps {
  isSelected?: boolean;
  link: Link;
  root: string;
}
const Anchor = ({ isSelected, link, root }: AnchorProps) => {
  const classes = [`${root}__item__link`];
  if (isSelected) {
    classes.push(`${root}__item__link--selected`);
  }

  return (
    <a href={link.url} className={classes.join(' ')}>
      <span className={`${root}__item__link__hr`} />
      <span className={`${root}__item__link__text`}>
        {link.title}
      </span>
    </a>
  );
};

interface ButtonProps extends AnchorProps {
  onItemClick: (ev: SyntheticEvent<HTMLButtonElement>) => void;
}
const Button = ({
  isSelected,
  link,
  onItemClick,
  root,
}: ButtonProps) => {
  const classes = [`${root}__item__link`, `${root}__item__link--button`];
  if (isSelected) {
    classes.push(`${root}__item__link--selected`);
  }

  return (
    <button
      type="button"
      className={classes.join(' ')}
      onClick={(ev) => onItemClick(ev)}
    >
      <span className={`${root}__item__link__hr`} />
      <span className={`${root}__item__link__text`}>
        {link.title}
      </span>
    </button>
  );
};

interface HeroCarouselProps {
  items: Link[];
  mode?: 'link' | 'button';
  onItemClick?: (index: number) => void;
  onScrollEnd?: (index: number) => void;
  selectedIndex?: number;
}
const HeroCarousel = ({
  items,
  mode = 'link',
  onItemClick,
  onScrollEnd,
  selectedIndex,
}: HeroCarouselProps) => {
  const root = 'hero-carousel';
  const scrollRef = useRef<HTMLOListElement>(null);
  const onScroll = useCallback(
    debounce(() => {
      if (scrollRef.current) {
        const { firstElementChild, scrollLeft } = scrollRef.current;
        const itemWidth = (firstElementChild?.clientWidth ?? 1);
        const index = Math.round(scrollLeft / itemWidth);
        onScrollEnd?.(index);
      }
    }, 100, {
      leading: false,
    }),
    [scrollRef.current],
  );

  useEffect(() => {
    if (scrollRef.current && selectedIndex !== undefined) {
      const element = scrollRef.current.children[selectedIndex] as HTMLLIElement;

      if (selectedIndex) {
        scrollRef.current?.scrollTo({ left: element?.offsetLeft, behavior: 'smooth' });
      } else {
        scrollRef.current?.scrollTo({ left: 0 - element?.offsetLeft, behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  return (
    <div className={root}>
      <ol className={`${root}__container`} ref={scrollRef} onScroll={onScroll}>
        {items.map((link, i) => (
          <li key={link.url} className={`${root}__item`}>
            {mode === 'link' ? (
              <Anchor
                link={link}
                root={root}
                isSelected={selectedIndex === i}
              />
            ) : (
              <Button
                link={link}
                onItemClick={(ev) => {
                  const el = ev.currentTarget;
                  scrollRef.current?.scrollTo({ left: el?.offsetLeft, behavior: 'smooth' });
                  onItemClick?.(i);
                }}
                root={root}
                isSelected={selectedIndex === i}
              />
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default HeroCarousel;
